/** The minimal shape this constraint reads, generic over the FormData rather than importing it back. */
type TemperatureShape = {
	readonly isTemperatureOverridden: boolean;
	readonly includesAccountOnlyFields: boolean;
	readonly temperatureBounds: { readonly minimum: number; readonly maximum: number };
};

/**
 * Only runs when the switch is on: an inherited (non-overridden) field is never sent. A number has no
 * way to carry "explicitly emptied" separately from "never set" — `0` is itself a real value — so a
 * `null` value is always an absence, never a failure on its own.
 *
 * `includesAccountOnlyFields` is not a stand-in for "which request shape are we building" here: the
 * temperature is `editable` **only** at the account level (plan §7.2), so this flag also happens to be
 * the only level where the field is actually writable. At the account level, `isTemperatureOverridden`
 * is forced regardless of `own`, so `null` there simply means "nobody has configured this yet" —
 * never an incomplete edit. At organization/facility level the field is read-only, so a `null` reaching
 * this constraint while `isTemperatureOverridden` is `true` can only be **constructed** (a stale/crafted
 * `FormData`), never produced by the UI itself; it is treated as invalid rather than silently accepted,
 * the same reasoning as the emptied-`InputNumber` case this constraint was written to catch (the case
 * that ruled out `ignoreNullValue`, see the class docblock). Bounds are checked only once a real number
 * is present.
 */
function isTemperatureValid<T extends TemperatureShape>(value: number | null, formData: T): boolean {
	if (!formData.isTemperatureOverridden) {
		return true;
	}

	if (value === null) {
		return formData.includesAccountOnlyFields;
	}

	return value >= formData.temperatureBounds.minimum && value <= formData.temperatureBounds.maximum;
}

export default isTemperatureValid;
